import React, { useEffect, useMemo, useState, createElement } from 'react';
import { FileText, Download, Users, TrendingUp, ArrowUpRight, ArrowDownRight, Calendar, ClipboardCheck, AlertTriangle } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { StudentData } from './StudentDetailPanel';
import { GroupId } from '../../../config/systemConfig';
import { MODULES } from '../../../content';
import { getClassModuleStats } from '../../../utils/activityHelpers';
import { StudentReportCard } from '../components/StudentReportCard';
import { getAssessmentHistory } from '../../../assessment/utils/assessmentStorage';
import type { AssessmentHistoryEntry } from '../../../assessment';
interface ReportsPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
}
const PERIODS = [{
  key: 7,
  label: 'week'
}, {
  key: 30,
  label: 'maand'
}, {
  key: 90,
  label: 'trimester'
}, {
  key: 365,
  label: 'jaar'
}];
function getPeriodStats(questionStates: Record<string, any>, familyMastery: Record<string, any>, group: number, periodDays: number) {
  const now = Date.now();
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const periodMs = periodDays * MS_PER_DAY;
  const currentStart = now - periodMs;
  const previousStart = currentStart - periodMs;
  const uniqueDays = new Set<string>();
  for (const state of Object.values(questionStates || {})) {
    if (!state.history) continue;
    for (const attempt of state.history) {
      if (attempt.timestamp >= currentStart && attempt.timestamp <= now) {
        const dateStr = new Date(attempt.timestamp).toISOString().split('T')[0];
        uniqueDays.add(dateStr);
      }
    }
  }
  let currentCorrect = 0;
  let currentTotal = 0;
  let previousCorrect = 0;
  let previousTotal = 0;
  for (const [key, state] of Object.entries(familyMastery || {})) {
    if (!key.includes(`_g${group}`)) continue;
    if (!state.history) continue;
    for (const attempt of state.history) {
      if (attempt.timestamp >= currentStart && attempt.timestamp <= now) {
        currentTotal++;
        if (attempt.isCorrect) currentCorrect++;
      } else if (attempt.timestamp >= previousStart && attempt.timestamp < currentStart) {
        previousTotal++;
        if (attempt.isCorrect) previousCorrect++;
      }
    }
  }
  let trendDelta = 0;
  if (currentTotal >= 3 && previousTotal >= 3) {
    const currentAcc = currentCorrect / currentTotal;
    const previousAcc = previousCorrect / previousTotal;
    trendDelta = Math.round((currentAcc - previousAcc) * 100);
  }
  return {
    daysPracticed: uniqueDays.size,
    trendDelta
  };
}
export function ReportsPage({
  students,
  selectedGroup,
  getModuleMastery
}: ReportsPageProps) {
  const [periodDays, setPeriodDays] = useState<number>(7);
  const [assessmentHistory, setAssessmentHistory] = useState<AssessmentHistoryEntry[]>([]);
  useEffect(() => {
    getAssessmentHistory(selectedGroup).then(setAssessmentHistory);
  }, [selectedGroup]);
  const enrichedStudents = useMemo(() => {
    return students.map(student => {
      let totalMastery = 0;
      let practicedCount = 0;
      let lowestMastery = 100;
      let highestMastery = 0;
      let weakestModule = null;
      let strongestModule = null;
      MODULES.forEach(module => {
        const mastery = getModuleMastery(student.id, module.id);
        if (mastery > 0) {
          totalMastery += mastery;
          practicedCount++;
          if (mastery < lowestMastery) {
            lowestMastery = mastery;
            weakestModule = module;
          }
          if (mastery > highestMastery) {
            highestMastery = mastery;
            strongestModule = module;
          }
        }
      });
      const avgMastery = practicedCount > 0 ? Math.round(totalMastery / practicedCount) : 0;
      const {
        daysPracticed,
        trendDelta
      } = getPeriodStats(student.data.questionStates || {}, student.data.familyMastery || {}, selectedGroup, periodDays);
      return {
        ...student,
        avgMastery,
        lowestMastery,
        weakestModule,
        strongestModule,
        daysPracticed,
        trendDelta
      };
    });
  }, [students, getModuleMastery, selectedGroup, periodDays]);
  const moduleStats = useMemo(() => {
    return getClassModuleStats(students, selectedGroup, getModuleMastery);
  }, [students, selectedGroup, getModuleMastery]);
  const summaryStats = useMemo(() => {
    if (enrichedStudents.length === 0) return {
      classAvg: 0,
      activeThisPeriod: 0
    };
    const totalAvg = enrichedStudents.reduce((sum, s) => sum + s.avgMastery, 0);
    const classAvg = Math.round(totalAvg / enrichedStudents.length);
    const activeThisPeriod = enrichedStudents.filter(s => s.daysPracticed > 0).length;
    return {
      classAvg,
      activeThisPeriod
    };
  }, [enrichedStudents]);
  const activePeriodLabel = PERIODS.find(p => p.key === periodDays)?.label || 'week';
  const handleExportClassListCsv = () => {
    const headers = ['Naam', 'Gemiddelde Beheersing (%)', 'Trend (%)', `Dagen Geoefend (deze ${activePeriodLabel})`, 'Sterkste Vak', 'Zwakste Vak'];
    const rows = enrichedStudents.map(s => {
      return [`"${s.name}"`, s.avgMastery, s.trendDelta, s.daysPracticed, `"${s.strongestModule?.title || ''}"`, `"${s.weakestModule?.title || ''}"`].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    downloadCsv(csvContent, 'klassenlijst');
  };
  const handleExportSubjectsCsv = () => {
    const headers = ['Vak', 'Klasgemiddelde (%)', 'Leerlingen Geoefend', 'Trend'];
    const rows = moduleStats.map(m => {
      const trendText = m.trend === 'up' ? 'Stijgt' : m.trend === 'down' ? 'Daalt' : 'Stabiel';
      return [`"${m.moduleTitle}"`, m.avgMastery, m.practicedCount, `"${trendText}"`].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    downloadCsv(csvContent, 'vakken_overzicht');
  };
  const handleExportAssessmentCsv = () => {
    const headers = ['Datum', 'Score (%)', 'Aantal Vragen', 'Tijd (min)'];
    const rows = assessmentHistory.map(a => {
      const dateStr = new Date(a.completedAt).toLocaleDateString('nl-NL');
      return [dateStr, a.score, a.totalQuestions, Math.round(a.totalTimeSeconds / 60)].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    downloadCsv(csvContent, 'toetsresultaten');
  };
  const avgAssessmentScore = assessmentHistory.length > 0 ? Math.round(assessmentHistory.reduce((sum, a) => sum + a.score, 0) / assessmentHistory.length) : 0;
  const downloadCsv = (content: string, filenamePrefix: string) => {
    const blob = new Blob([content], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${filenamePrefix}_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return <PageLayout title="Rapporten" description="Exporteer data en rapportages" data-id="element-5039">
      <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-5040">
        {/* Summary Strip */}
        <Card className="bg-white border-slate-200 shadow-sm overflow-hidden" data-id="element-5041">
          <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-slate-100" data-id="element-5042">
            <div className="flex-1 p-4 flex items-center gap-4" data-id="element-5043">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-5044">
                <Users className="w-5 h-5" data-id="element-5045" />
              </div>
              <div data-id="element-5046">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-5047">
                  Leerlingen
                </div>
                <div className="text-2xl font-black text-slate-900" data-id="element-5048">
                  {students.length}
                </div>
              </div>
            </div>

            <div className="flex-1 p-4 flex items-center gap-4" data-id="element-5049">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0" data-id="element-5050">
                <TrendingUp className="w-5 h-5" data-id="element-5051" />
              </div>
              <div data-id="element-5052">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-5053">
                  Klasgemiddelde
                </div>
                <div className="text-2xl font-black text-slate-900" data-id="element-5054">
                  {summaryStats.classAvg}%
                </div>
              </div>
            </div>

            <div className="flex-1 p-4 flex items-center gap-4" data-id="element-5055">
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0" data-id="element-5056">
                <Calendar className="w-5 h-5" data-id="element-5057" />
              </div>
              <div className="min-w-0" data-id="element-5058">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-5059">
                  Actief deze {activePeriodLabel}
                </div>
                <div className="text-2xl font-black text-slate-900" data-id="element-5060">
                  {summaryStats.activeThisPeriod}{' '}
                  <span className="text-sm text-slate-400 font-bold" data-id="element-5061">
                    / {students.length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Period Selector */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide" data-id="element-5062">
          {PERIODS.map(period => {
          const isActive = periodDays === period.key;
          return <button key={period.key} onClick={() => setPeriodDays(period.key)} className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${isActive ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700'}`} data-id="element-5063">
                Deze {period.label}
              </button>;
        })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5064">
          {/* Klas-overzicht Section */}
          <Card className="p-5 border-slate-200 shadow-sm bg-white flex flex-col" data-id="element-5065">
            <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center mb-3 border border-indigo-100" data-id="element-5066">
              <FileText className="w-6 h-6" data-id="element-5067" />
            </div>
            <h2 className="text-xl font-black text-slate-900 mb-2" data-id="element-5068">
              Klas-overzicht
            </h2>
            <p className="text-slate-500 mb-5 flex-1" data-id="element-5069">
              Exporteer de huidige status van de hele klas. Handig voor je eigen
              administratie of om te bespreken met collega's.
            </p>

            <div className="space-y-3" data-id="element-5070">
              <Button onClick={handleExportClassListCsv} variant="outline" className="w-full justify-start text-left border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300" data-id="element-5071">
                <Download className="w-4 h-4 mr-3 text-indigo-500" data-id="element-5072" />
                <div data-id="element-5073">
                  <div className="font-bold text-slate-900" data-id="element-5074">
                    Klassenlijst CSV
                  </div>
                  <div className="text-xs text-slate-500 font-normal" data-id="element-5075">
                    Leerlingen, gemiddelde beheersing, activiteit
                  </div>
                </div>
              </Button>

              <Button onClick={handleExportSubjectsCsv} variant="outline" className="w-full justify-start text-left border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300" data-id="element-5076">
                <Download className="w-4 h-4 mr-3 text-indigo-500" data-id="element-5077" />
                <div data-id="element-5078">
                  <div className="font-bold text-slate-900" data-id="element-5079">
                    Vakken-overzicht CSV
                  </div>
                  <div className="text-xs text-slate-500 font-normal" data-id="element-5080">
                    Klasgemiddelde per vak, trends
                  </div>
                </div>
              </Button>
            </div>
          </Card>

          {/* Per-leerling rapport Section */}
          <StudentReportCard students={enrichedStudents} selectedGroup={selectedGroup} getModuleMastery={getModuleMastery} periodDays={periodDays} data-id="element-5081" />

          {/* Toetsresultaten Section */}
          <Card className="p-5 border-slate-200 shadow-sm bg-white flex flex-col md:col-span-2" data-id="element-5082">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-5" data-id="element-5083">
              <div data-id="element-5084">
                <div className="w-12 h-12 bg-orange-50 text-orange-600 rounded-xl flex items-center justify-center mb-3 border border-orange-100" data-id="element-5085">
                  <ClipboardCheck className="w-6 h-6" data-id="element-5086" />
                </div>
                <h2 className="text-xl font-black text-slate-900 mb-2" data-id="element-5087">
                  Toetsresultaten
                </h2>
                <p className="text-slate-500" data-id="element-5088">
                  Overzicht van alle gemaakte toetsen in deze groep.
                </p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-start gap-2 max-w-xs" data-id="element-5089">
                <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" data-id="element-5090" />
                <div className="text-xs text-orange-800" data-id="element-5091">
                  <strong data-id="element-5092">Indicatieve niveaubepaling</strong> — geen officiële
                  Cito-toets.
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6" data-id="element-5093">
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 text-center" data-id="element-5094">
                <div className="text-2xl font-black text-slate-900" data-id="element-5095">
                  {assessmentHistory.length}
                </div>
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-1" data-id="element-5096">
                  Toetsen Gemaakt
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 text-center" data-id="element-5097">
                <div className="text-2xl font-black text-slate-900" data-id="element-5098">
                  {avgAssessmentScore}%
                </div>
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-1" data-id="element-5099">
                  Gemiddelde Score
                </div>
              </div>
            </div>

            <div className="mt-auto pt-4 border-t border-slate-100" data-id="element-5100">
              <Button onClick={handleExportAssessmentCsv} variant="outline" disabled={assessmentHistory.length === 0} className="w-full sm:w-auto justify-start text-left border-orange-200 text-orange-600 hover:bg-orange-50 hover:border-orange-300" data-id="element-5101">
                <Download className="w-4 h-4 mr-3 text-orange-500" data-id="element-5102" />
                <div data-id="element-5103">
                  <div className="font-bold text-slate-900" data-id="element-5104">
                    Toetsresultaten CSV
                  </div>
                  <div className="text-xs text-slate-500 font-normal" data-id="element-5105">
                    Datum, score, aantal vragen, tijd
                  </div>
                </div>
              </Button>
            </div>
          </Card>
        </div>

        {/* Week-over-week Vergelijking */}
        {enrichedStudents.length > 0 && <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-5106">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between" data-id="element-5107">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-5108">
                Periode vergelijking
              </h3>
              <span className="text-[10px] text-slate-400 font-medium" data-id="element-5109">
                Deze {activePeriodLabel} vs. vorige {activePeriodLabel}
              </span>
            </div>
            {(() => {
          const sorted = [...enrichedStudents].sort((a, b) => b.trendDelta - a.trendDelta);
          const improvers = sorted.filter(s => s.trendDelta > 0);
          const stable = sorted.filter(s => s.trendDelta === 0);
          const decliners = sorted.filter(s => s.trendDelta < 0);
          const renderRow = (student: (typeof enrichedStudents)[0], i: number, bgAlt: boolean) => <div key={student.id} className={`grid grid-cols-[1fr_auto_auto] gap-4 items-center px-4 py-2.5 ${bgAlt ? 'bg-slate-50/30' : 'bg-white'}`} data-id="element-5110">
                  <div className="font-bold text-sm text-slate-900 truncate" data-id="element-5111">
                    {student.name}
                  </div>
                  <div className={`font-black text-sm tabular-nums ${student.avgMastery >= 70 ? 'text-emerald-600' : student.avgMastery >= 40 ? 'text-amber-600' : 'text-rose-600'}`} data-id="element-5112">
                    {student.avgMastery}%
                  </div>
                  <div className="flex items-center gap-1 min-w-[60px] justify-end" data-id="element-5113">
                    {student.trendDelta > 0 && <>
                        <ArrowUpRight className="w-3.5 h-3.5 text-emerald-500" data-id="element-5114" />
                        <span className="text-xs font-bold text-emerald-600 tabular-nums" data-id="element-5115">
                          +{student.trendDelta}%
                        </span>
                      </>}
                    {student.trendDelta < 0 && <>
                        <ArrowDownRight className="w-3.5 h-3.5 text-rose-500" data-id="element-5116" />
                        <span className="text-xs font-bold text-rose-600 tabular-nums" data-id="element-5117">
                          {student.trendDelta}%
                        </span>
                      </>}
                    {student.trendDelta === 0 && <span className="text-xs font-medium text-slate-400 tabular-nums" data-id="element-5118">
                        —
                      </span>}
                  </div>
                </div>;
          return <div data-id="element-5119">
                  {improvers.length > 0 && <>
                      <div className="px-4 py-1.5 bg-emerald-50/60 border-b border-emerald-100" data-id="element-5120">
                        <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider" data-id="element-5121">
                          ↑ Stijgers ({improvers.length})
                        </span>
                      </div>
                      <div className="divide-y divide-slate-100" data-id="element-5122">
                        {improvers.map((s, i) => renderRow(s, i, i % 2 !== 0))}
                      </div>
                    </>}
                  {stable.length > 0 && <>
                      <div className="px-4 py-1.5 bg-slate-50 border-y border-slate-100" data-id="element-5123">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-5124">
                          → Stabiel ({stable.length})
                        </span>
                      </div>
                      <div className="divide-y divide-slate-100" data-id="element-5125">
                        {stable.map((s, i) => renderRow(s, i, i % 2 !== 0))}
                      </div>
                    </>}
                  {decliners.length > 0 && <>
                      <div className="px-4 py-1.5 bg-rose-50/60 border-y border-rose-100" data-id="element-5126">
                        <span className="text-[10px] font-bold text-rose-700 uppercase tracking-wider" data-id="element-5127">
                          ↓ Dalers ({decliners.length})
                        </span>
                      </div>
                      <div className="divide-y divide-slate-100" data-id="element-5128">
                        {decliners.map((s, i) => renderRow(s, i, i % 2 !== 0))}
                      </div>
                    </>}
                </div>;
        })()}
          </Card>}
      </div>
    </PageLayout>;
}